import * as React from 'react';
import cn from 'classnames';
import { detectExtension } from 'mimetypes';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useAuthUser } from 'react-auth-kit';
import { useAccessToken } from 'src/hooks/useAccessToken';
import useRoleManager from 'src/hooks/useRoleManager';
import { useImageEditAvailable } from 'src/hooks/useImageEditAvailable';
import { usePrintExportAvailable } from 'src/hooks/usePrintExportAvailable';
import { useFindDuplicates } from 'src/hooks/useFindDuplicates';
import useModal from 'src/hooks/useModal';
import PrintExportDialog from 'src/pages/Asset/components/PrintExportDialog';
import AssetLinkDialog from 'src/components/AssetLinkDialog/AssetLinkDialog';
import ChangeThumbnailDialog from 'src/components/ChangeThumbnailDialog/ChangeThumbnailDialog';

import { deleteAsset, getAssetMediaUrl, restoreAsset } from 'src/api/functions/assetsApi';
import Checkbox from '../Checkbox';
import Dropdown from '../Dropdown';
import {
  PlayBox,
  FileArrowDown,
  Eye,
  TrashSimple,
  ArrowCounterClockwise,
  ArrowLeftCircle,
  Gear,
  Stack,
  DotsThreeVertical,
  ChatAI,
  Printer,
  AssetGetLink,
  ImageSquare,
  Copy,
} from 'src/common/icons';
import { isPreviewPlayable } from 'src/utils/previews';
import { humanFileSize } from 'src/utils/transform';

import styles from './ListingCard.module.scss';
import { useState } from 'react';
import { useAlerts } from 'src/common/AlertManager';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import VersionBadge from 'src/components/VersionBadge';
import { AssetVersionsSummary } from 'src/types/TAssets';
import ReactTooltip from 'react-tooltip';
import { useAtom } from 'jotai';
import { deletedAssetsAtom } from 'src/shared/deletedAssets';
import AssetPreview from '../AssetPreview';
import { useCollectionMutation } from 'src/api/mutations/collectionMutations';
import { useCollectionQuery } from 'src/api/queries/collectionsQueries';
import { queryNames } from 'src/api/utils/queryNames';
import { CollectionResponse } from 'src/types/TCollection';
import { useQueryClient } from 'react-query';
import { addCacheBuster } from 'src/utils/previews';


type ListingCardProps = {
  className?: string;
  id: string;
  siblings?: string[];
  imageUrl: string;
  name: string;
  size: string;
  trashed: boolean;
  status: string;
  contentType: string;
  isSelected?: boolean;
  isGenerated?: boolean;
  // For generated assets: id of the source asset they were derived from.
  originalAssetId?: string;
  // Content versioning summary from the search result (`versions`)
  versions?: AssetVersionsSummary | null;
  error?: any;
  onSelectChange?: React.ChangeEventHandler<HTMLInputElement> & ((value: React.ChangeEvent<HTMLInputElement>) => void);
};

export const ListingCard: React.FC<ListingCardProps> = ({
  className,
  id,
  siblings,
  imageUrl,
  name,
  size,
  trashed,
  contentType,
  isSelected,
  isGenerated,
  originalAssetId,
  versions,
  onSelectChange,
  status,
  error,
  ...rest
}) => {
  const location = useLocation();
  const findDuplicates = useFindDuplicates();
  const downloadingRef = React.useRef('');
  const auth = useAuthUser();
  const accessToken = useAccessToken();
  const { isUser } = useRoleManager();
  const imageEditAvailable = useImageEditAvailable();
  const printExportAvailable = usePrintExportAvailable();
  const { showModal } = useModal();

  const { showSuccess, showAlert } = useAlerts();
  const { getErrorMessage } = useErrorMessages();

  const [isDeletingAsset, setIsDeletingAsset] = useState(false);
  const [isRestoringAsset, setIsRestoringAsset] = useState(false);

  const [deletedAssets, updateDeletedAssets] = useAtom(deletedAssetsAtom);
  const queryClient = useQueryClient();

  const { data: basketData } = useCollectionQuery(undefined, {
    staleTime: 1000 * 60 * 5,
  });

  const basketMutation = useCollectionMutation(Number(basketData?.data?.id), {
    onError: (error, newData, context: any) => {
      queryClient.setQueryData([queryNames.collection, null], context.previousData);

      showAlert(getErrorMessage(error.message));
    },
    onMutate: (data) => {
      /*showInfo(`Requesting update...`);*/
      const previousData = queryClient.getQueryData<CollectionResponse>([queryNames.collection, null]);
      queryClient.setQueryData<CollectionResponse | undefined>(
        [queryNames.collection, null],
        (old: CollectionResponse | undefined) => {
          if (typeof old !== 'undefined') {
            old.data.assets = (old?.data?.assets ?? [])
              .filter((id) => !(data?.deletions ?? []).includes(id))
              .concat(data?.additions ?? []);
          }
          return old;
        },
      );
      return { previousData };
    },
    onSuccess: () => {
      showSuccess('Successfully added asset to basket');
    },
  });

  const getMediaUrl = async () => {
    if (downloadingRef.current === '') {
      const url = await getAssetMediaUrl(id, 'original', auth()!.accessId, 'download', accessToken);
      downloadingRef.current = url;
      window.open(url);
    } else {
      window.open(downloadingRef.current);
    }
  };

  const handleRestoreAsset = async () => {
    if (isRestoringAsset) return;

    setIsRestoringAsset(true);
    try {
      const response = await restoreAsset(id, accessToken);

      if (typeof response.status === 'number' && !([200,202].includes(response.status))) {
        throw response;
      }

      showSuccess(`Successfully restored asset.`);
    } catch (e: any) {
      showAlert(getErrorMessage(e.message));
    } finally {
      setIsRestoringAsset(false);
    }
  };

  const handleDeleteAsset = async () => {
    if (isDeletingAsset) return;

    setIsDeletingAsset(true);
    try {
      const response = await deleteAsset(id, accessToken);

      if (typeof response.status === 'number' && !([200,202].includes(response.status))) {
        throw response;
      }

      showSuccess(`Successfully deleted asset.`);
      updateDeletedAssets([...deletedAssets, id]);
    } catch (e: any) {
      showAlert(getErrorMessage(e.message));
    } finally {
      setIsDeletingAsset(false);
    }
  };

  const handleAddToBasket = () => {
    if (basketMutation.isLoading) return;
    basketMutation.mutateAsync({ additions: [id] });
  };

  // Add cache buster to imageUrl to ensure fresh thumbnails
  const cachedImageUrl = React.useMemo(() => addCacheBuster(imageUrl), [imageUrl]);

  const history = useHistory();

  // Actions menu (`...`). Replaces the legacy hover-only overlay on
  // both desktop and mobile so the same affordance works on touch and
  // pointer devices.
  const handleMenuClick = React.useCallback(
    (key: string) => {
      switch (key) {
        case 'view':
          history.push(`/asset/${id}`, { background: location, siblings });
          break;
        case 'aiImageEdit':
          history.push(`/asset/${id}`, { background: location, siblings, openAIEdit: true });
          break;
        case 'printExport':
          showModal(<PrintExportDialog assetId={id} assetName={name} />);
          break;
        case 'viewOriginal':
          if (originalAssetId) {
            history.push(`/asset/${originalAssetId}`, { background: location });
          }
          break;
        case 'download':
          getMediaUrl();
          break;
        case 'getLink':
          showModal(<AssetLinkDialog assetId={id} assetName={name} />);
          break;
        case 'findDuplicates':
          findDuplicates({ id, name });
          break;
        case 'changeThumbnail':
          showModal(<ChangeThumbnailDialog assetId={id} />);
          break;
        case 'addToBasket':
          handleAddToBasket();
          break;
        case 'restore':
          handleRestoreAsset();
          break;
        case 'delete':
          handleDeleteAsset();
          break;
        default:
          break;
      }
    },
    [history, id, name, location, siblings, originalAssetId, getMediaUrl, handleAddToBasket, handleRestoreAsset, handleDeleteAsset, showModal, findDuplicates],
  );

  const menuOptions = React.useMemo(() => {
    const opts: Array<{ key: string; value: React.ReactNode; icon?: React.ReactNode }> = [];
    if (!trashed) {
      opts.push({ key: 'view', value: 'View', icon: <Eye /> });
      // One-asset lookup, so it lives in this menu (not in "With Selected"),
      // near the top as in the list row's menu.
      opts.push({ key: 'findDuplicates', value: 'Find duplicates', icon: <Copy /> });
      if (!isUser && contentType?.startsWith('image/') && imageEditAvailable) {
        opts.push({ key: 'aiImageEdit', value: 'AI Image Edit', icon: <ChatAI /> });
      }
      if (!isUser && contentType?.startsWith('image/') && printExportAvailable) {
        opts.push({ key: 'printExport', value: 'Export for Print', icon: <Printer /> });
      }
      opts.push({ key: 'download', value: 'Download', icon: <FileArrowDown /> });
      opts.push({ key: 'getLink', value: 'Get link / Embed', icon: <AssetGetLink /> });
      if (isGenerated && originalAssetId) {
        opts.push({ key: 'viewOriginal', value: 'View original', icon: <ArrowLeftCircle /> });
      }
      opts.push({ key: 'addToBasket', value: 'Add to basket', icon: <Stack /> });
    }
    if (!isUser) {
      if (trashed) {
        opts.push({ key: 'restore', value: 'Restore', icon: <ArrowCounterClockwise /> });
      } else {
        opts.push({ key: 'changeThumbnail', value: 'Change thumbnail', icon: <ImageSquare /> });
        opts.push({ key: 'delete', value: 'Delete', icon: <TrashSimple /> });
      }
    }
    return opts;
  }, [trashed, isUser, isGenerated, originalAssetId, contentType, imageEditAvailable, printExportAvailable]);

  return (
    <div className={cn([styles.ListingCard, className])}>
      {/* Actions menu (`...`). Rendered as a sibling of the thumbnail
          (not inside it) because `.thumbWrapper` has `overflow: hidden`
          for image clipping, which would otherwise chop off the
          dropdown's options popup. Visible on every viewport — the
          legacy hover overlay is hidden so the same affordance works
          for touch and pointer devices. */}
      {menuOptions.length > 0 ? (
        <div className={styles.menu} onClick={(e) => e.stopPropagation()}>
          <Dropdown
            variant="menu"
            // `left` anchors the menu's left edge to the trigger so the
            // options expand to the right; with the trigger at the top-
            // left of the card, anchoring right would push the menu off
            // the viewport for left-column cards.
            dropdownAnchor="left"
            icon={<DotsThreeVertical />}
            options={menuOptions}
            onClick={handleMenuClick}
          />
        </div>
      ) : null}

      <div className={styles.thumbWrapper}>
        {status === 'NOT_READY' && (
          <div className={styles.processingAsset}>
            <Gear /> <span>Processing</span>
          </div>
        )}

        {isGenerated && status !== 'NOT_READY' && (
          <span
            className={styles.generatedBadge}
            data-tip="Generated asset"
            aria-label="Generated asset"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 2l1.8 5.4L19 9l-5.2 1.6L12 16l-1.8-5.4L5 9l5.2-1.6L12 2z" />
              <path d="M19 14l.9 2.7L22 18l-2.1.6L19 21l-.9-2.4L16 18l2.1-1L19 14z" opacity="0.85" />
            </svg>
            <ReactTooltip effect="solid" />
          </span>
        )}

        {status !== 'NOT_READY' && <VersionBadge versions={versions} />}

        {/* Tap-to-view target sits inside the thumbnail so the tappable
            area matches the image. */}
        {!trashed && (
          <Link
            className={styles.tapTarget}
            to={{
              pathname: `/asset/${id}`,
              state: { background: location, siblings },
            }}
            aria-label={`View ${name}`}
          />
        )}

        <AssetPreview src={cachedImageUrl} name={name} status={status} contentType={contentType} />
        {contentType && status !== 'NOT_READY' && isPreviewPlayable(contentType) && (
          <div className={styles.playbox}>
            <PlayBox />
          </div>
        )}
      </div>

      <div className={styles.titleWrapper}>
        <div className={styles.checkbox}>
          <Checkbox checked={isSelected} onChange={onSelectChange} />
        </div>
        <div className={styles.title}>
          <div className={styles.titleHolder}>
            {error?.original && (
              <button data-tip={error?.original} className={styles.errorAsset}>
                <span className={styles.errorTitleStyle}>ERROR</span>
                <ReactTooltip effect="solid" />
              </button>
            )}
            {!trashed ? (
              <Link
                to={{
                  pathname: `/asset/${id}`,
                  state: { background: location, siblings },
                }}
              >
                {name}
              </Link>
            ) : (
              <span>{name}</span>
            )}
          </div>
          <small>
            {detectExtension(contentType).toUpperCase()} {humanFileSize(Number(size))}
          </small>
        </div>
      </div>
    </div>
  );
};

export default ListingCard;
